import React from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import {
  BriefcaseBusiness,
  HeartPulse,
  MessageSquareText,
  Scale,
  ShieldCheck,
  Users,
} from "lucide-react";
import type { CommunityOverview } from "lib/api/community";
import type { CommunityUiStrings } from "i18n/communityUi";
import SectionShell from "../SectionShell";
import {
  SectionErrorState,
  SectionLoadingState,
} from "../SectionStates";
import { formatCommunityWindow } from "../utils";

interface EnterprisePulseProps {
  query: UseQueryResult<CommunityOverview>;
  labels: CommunityUiStrings;
  numberFormatter: Intl.NumberFormat;
}

interface DeltaBadgeProps {
  value: number;
  labels: CommunityUiStrings;
  numberFormatter: Intl.NumberFormat;
  isPercent?: boolean;
}

function formatDeltaValue(
  value: number,
  numberFormatter: Intl.NumberFormat,
  labels: CommunityUiStrings,
  isPercent = false
): string {
  if (isPercent) {
    return `${Math.abs(value).toFixed(1)} ${labels.deltaPoints}`;
  }

  return numberFormatter.format(Math.abs(Math.round(value)));
}

const DeltaBadge: React.FC<DeltaBadgeProps> = ({
  value,
  labels,
  numberFormatter,
  isPercent = false,
}) => {
  const tone =
    value > 0
      ? "border-emerald-300/70 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-950/30 dark:text-emerald-200"
      : value < 0
        ? "border-amber-300/70 bg-amber-50 text-amber-700 dark:border-amber-500/40 dark:bg-amber-950/30 dark:text-amber-200"
        : "border-eoe-border/70 bg-eoe-bg-hover/60 text-eoe-text-sub dark:border-eoe-dark-border/70 dark:bg-eoe-dark-bg-hover/30 dark:text-eoe-dark-text-sub";
  const label = value > 0 ? labels.deltaUp : value < 0 ? labels.deltaDown : labels.deltaFlat;

  return (
    <span
      className={`inline-flex w-fit items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium ${tone}`}
    >
      <span>{label}</span>
      <span>{formatDeltaValue(value, numberFormatter, labels, isPercent)}</span>
      <span>{labels.deltaFromPreviousWindow}</span>
    </span>
  );
};

/**
 * Hero KPI grid for the community enterprise dashboard.
 */
const EnterprisePulse: React.FC<EnterprisePulseProps> = ({
  query,
  labels,
  numberFormatter,
}) => {
  return (
    <SectionShell
      title={labels.enterprisePulseTitle}
      subtitle={labels.enterprisePulseSubtitle}
      contentClassName="space-y-5"
    >
      {query.isPending ? (
        <SectionLoadingState message={labels.loading} />
      ) : query.isError ? (
        <SectionErrorState
          message={labels.sectionError}
          retryLabel={labels.retry}
          onRetry={() => {
            void query.refetch();
          }}
          isRetrying={query.isRefetching}
        />
      ) : query.data ? (
        <>
          <div className="flex flex-wrap items-center gap-2 text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
            <span>{formatCommunityWindow(labels.windowDays, query.data.window_days)}</span>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {[
              {
                key: "active_issues",
                label: labels.kpiActiveIssues,
                icon: BriefcaseBusiness,
                value: query.data.active_issues,
                delta: query.data.delta.active_issues,
                background:
                  "from-blue-500/10 via-blue-500/5 to-transparent dark:from-blue-400/10 dark:via-blue-400/5 dark:to-transparent",
              },
              {
                key: "active_contributors",
                label: labels.kpiActiveContributors,
                icon: Users,
                value: query.data.active_contributors,
                delta: query.data.delta.active_contributors,
                background:
                  "from-violet-500/10 via-violet-500/5 to-transparent dark:from-violet-400/10 dark:via-violet-400/5 dark:to-transparent",
              },
              {
                key: "total_engagements",
                label: labels.kpiTotalEngagements,
                icon: MessageSquareText,
                value: query.data.total_engagements,
                delta: query.data.delta.total_engagements,
                background:
                  "from-teal-500/10 via-teal-500/5 to-transparent dark:from-teal-400/10 dark:via-teal-400/5 dark:to-transparent",
              },
              {
                key: "pipeline_health_pct",
                label: labels.kpiPipelineHealth,
                icon: HeartPulse,
                value: query.data.pipeline_health_pct,
                delta: query.data.delta.pipeline_health_pct,
                background:
                  "from-amber-500/10 via-amber-500/5 to-transparent dark:from-amber-400/10 dark:via-amber-400/5 dark:to-transparent",
                isPercent: true,
              },
              {
                key: "active_teabates",
                label: labels.kpiActiveTeabates,
                icon: Scale,
                value: query.data.active_teabates,
                delta: query.data.delta.active_teabates,
                background:
                  "from-fuchsia-500/10 via-fuchsia-500/5 to-transparent dark:from-fuchsia-400/10 dark:via-fuchsia-400/5 dark:to-transparent",
              },
              {
                key: "governance_score",
                label: labels.kpiGovernanceScore,
                icon: ShieldCheck,
                value: query.data.governance_score,
                delta: query.data.delta.governance_score,
                background:
                  "from-emerald-500/10 via-emerald-500/5 to-transparent dark:from-emerald-400/10 dark:via-emerald-400/5 dark:to-transparent",
                isPercent: true,
              },
            ].map((card) => {
              const Icon = card.icon;
              const displayValue = card.isPercent
                ? `${card.value.toFixed(1)}%`
                : numberFormatter.format(card.value);

              return (
                <div
                  key={card.key}
                  className={`relative overflow-hidden rounded-2xl border border-eoe-border/80 bg-gradient-to-br ${card.background} p-4 dark:border-eoe-dark-border/80`}
                >
                  <div className="space-y-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-eoe-text-sub dark:text-eoe-dark-text-sub">
                          {card.label}
                        </p>
                        <p className="text-3xl font-semibold tracking-tight text-eoe-text dark:text-eoe-dark-text md:text-4xl">
                          {displayValue}
                        </p>
                      </div>
                      <div className="flex size-12 items-center justify-center rounded-2xl bg-white/80 text-eoe-text shadow-sm dark:bg-eoe-dark-bg/80 dark:text-eoe-dark-text">
                        <Icon className="size-5" aria-hidden />
                      </div>
                    </div>
                    <DeltaBadge
                      value={card.delta}
                      labels={labels}
                      numberFormatter={numberFormatter}
                      isPercent={card.isPercent}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <SectionLoadingState message={labels.loading} />
      )}
    </SectionShell>
  );
};

export default EnterprisePulse;

